import { useState } from 'react';
import {
  ArrowRight,
  Inbox,
  Heart,
  MessageCircle,
  TrendingUp,
  Wallet,
  Star,
  Clock,
  CheckCircle2,
  Briefcase,
} from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import {
  services,
  getVendorById,
  getFeaturedVendors,
  categories,
} from '../data/fixtures';
import {
  Button,
  PageSection,
  RatingLine,
  VerifiedBadge,
  Avatar,
} from '../shell/ui';

// ─── Shared helpers ────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        color: 'var(--color-accent-rosegold)',
        fontWeight: 500,
        marginBottom: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: 'var(--font-family-display)',
        fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: '-0.01em',
        margin: 0,
        marginBottom: '1.5rem',
      }}
    >
      {children}
    </h2>
  );
}

const panelStyle: React.CSSProperties = {
  background: 'var(--color-background-card)',
  border: '1px solid var(--color-border-light)',
  borderRadius: 'var(--radius-lg)',
  padding: '1.75rem',
};

function StatTile({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
}) {
  return (
    <div style={panelStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}
      >
        <span
          style={{
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--color-text-muted)',
            fontWeight: 600,
          }}
        >
          {label}
        </span>
        <div
          style={{
            width: '34px',
            height: '34px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-accent-rosegold-50)',
            color: 'var(--color-accent-rosegold)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
      </div>
      <div
        style={{
          fontFamily: 'var(--font-family-display)',
          fontSize: '2rem',
          fontWeight: 400,
          lineHeight: 1,
          letterSpacing: '-0.01em',
          color: 'var(--color-text-primary)',
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            marginTop: '0.5rem',
            fontSize: '0.8125rem',
            color: 'var(--color-text-muted)',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard switch screen ───────────────────────────────────────────────

export function DashboardScreen() {
  const [role, setRole] = useState<'customer' | 'vendor'>('customer');

  return (
    <>
      {/* Greeting band */}
      <section
        style={{
          background: 'var(--color-background-surface)',
          borderBottom: '1px solid var(--color-border-light)',
          padding: '3.5rem 0 3rem',
        }}
      >
        <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 1.5rem' }}>
          <Eyebrow>Dashboard</Eyebrow>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '2rem',
              flexWrap: 'wrap',
            }}
          >
            <h1
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 400,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                margin: 0,
                maxWidth: '640px',
              }}
            >
              Welcome back,{' '}
              <em style={{ fontStyle: 'italic', color: 'var(--color-accent-rosegold)' }}>
                {role === 'customer' ? 'Aygun' : 'Atelier Nord'}
              </em>
              .
            </h1>

            {/* Role switch */}
            <div
              style={{
                display: 'flex',
                gap: '0.375rem',
                padding: '0.25rem',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {(['customer', 'vendor'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    padding: '0.5rem 1rem',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    borderRadius: 'var(--radius-sm)',
                    background:
                      role === r ? 'var(--color-accent-rosegold)' : 'transparent',
                    color:
                      role === r
                        ? 'var(--color-text-inverse)'
                        : 'var(--color-text-muted)',
                    textTransform: 'capitalize',
                  }}
                >
                  {r} view
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {role === 'customer' ? <CustomerDashboardBody /> : <VendorDashboardBody />}
    </>
  );
}

// ─── Customer dashboard ────────────────────────────────────────────────────

function CustomerDashboardBody() {
  const { navigate } = useRouter();
  const featured = getFeaturedVendors(3);
  const savedServices = services.slice(0, 3);

  const activeRequests = [
    {
      id: 'r1',
      title: 'Kitchen renovation — 12 m²',
      category: 'Renovation',
      status: 'Receiving offers',
      offers: 4,
      budget: '14,000–18,000 ₼',
      posted: '2 days ago',
    },
    {
      id: 'r2',
      title: 'Full rewire, 3-bed apartment',
      category: 'Electrical',
      status: 'In review',
      offers: 2,
      budget: '4,500–6,500 ₼',
      posted: '5 days ago',
    },
  ];

  return (
    <PageSection paddingY="3rem">
      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1rem',
          marginBottom: '3rem',
        }}
        className="dash-stats"
      >
        <StatTile label="Active requests" value="2" hint="6 offers in total" icon={<Inbox size={16} />} />
        <StatTile label="Saved vendors" value="7" hint="2 new this week" icon={<Heart size={16} />} />
        <StatTile label="Unread messages" value="3" hint="From 2 studios" icon={<MessageCircle size={16} />} />
        <StatTile label="Completed" value="1" hint="Icherisheher duplex" icon={<CheckCircle2 size={16} />} />
      </div>

      {/* Two column body */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr',
          gap: '2rem',
          alignItems: 'start',
        }}
        className="dash-body"
      >
        {/* Left: active requests + saved */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: '1.25rem',
              }}
            >
              <SectionTitle>Your active requests</SectionTitle>
              <button
                onClick={() => navigate('request')}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  color: 'var(--color-accent-rosegold)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Post a new request →
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {activeRequests.map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate('offers')}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'block',
                    ...panelStyle,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                          marginBottom: '0.375rem',
                        }}
                      >
                        {r.category}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-family-display)',
                          fontSize: '1.375rem',
                          fontWeight: 400,
                          letterSpacing: '-0.005em',
                          color: 'var(--color-text-primary)',
                          lineHeight: 1.2,
                        }}
                      >
                        {r.title}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: '0.3125rem 0.75rem',
                        background: 'var(--color-accent-rosegold-50)',
                        color: 'var(--color-accent-rosegold)',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.status}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '1.5rem',
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-muted)',
                      flexWrap: 'wrap',
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--color-border-light)',
                    }}
                  >
                    <span><strong style={{ color: 'var(--color-text-primary)' }}>{r.offers}</strong> offers</span>
                    <span>Budget {r.budget}</span>
                    <span>Posted {r.posted}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <SectionTitle>Saved services</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {savedServices.map((s) => {
                const vendor = getVendorById(s.vendorId)!;
                return (
                  <button
                    key={s.id}
                    onClick={() => navigate('service', { serviceId: s.id })}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'center',
                      padding: '1rem',
                      background: 'var(--color-background-card)',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <div
                      style={{
                        width: '84px',
                        height: '64px',
                        borderRadius: 'var(--radius-sm)',
                        background: `url(${s.heroImage}) center/cover no-repeat`,
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '0.9375rem',
                          fontWeight: 500,
                          color: 'var(--color-text-primary)',
                          marginBottom: '0.1875rem',
                        }}
                      >
                        {s.title}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                        {vendor.name} · from {s.priceFrom.toLocaleString()} ₼
                      </div>
                    </div>
                    <ArrowRight size={16} style={{ color: 'var(--color-text-muted)' }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: recommended vendors + quick actions */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={panelStyle}>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                marginBottom: '1rem',
              }}
            >
              Quick actions
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <Button onClick={() => navigate('request')} style={{ width: '100%' }}>
                Post a new request
              </Button>
              <Button variant="secondary" onClick={() => navigate('browse')} style={{ width: '100%' }}>
                Browse categories
              </Button>
              <Button variant="ghost" onClick={() => navigate('messages')} style={{ width: '100%' }}>
                Open messages
              </Button>
            </div>
          </div>

          <div style={panelStyle}>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                marginBottom: '1rem',
              }}
            >
              Vendors you follow
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {featured.map((v) => (
                <button
                  key={v.id}
                  onClick={() => navigate('vendor', { vendorId: v.id })}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '0.875rem',
                    alignItems: 'center',
                  }}
                >
                  <Avatar src={v.avatar} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        marginBottom: '0.125rem',
                      }}
                    >
                      <span style={{ fontSize: '0.9375rem', fontWeight: 500 }}>{v.name}</span>
                      {v.verified && <VerifiedBadge />}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                      Replies {v.responseTime}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        @media (max-width: 1000px) {
          .dash-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .dash-body { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .dash-stats { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PageSection>
  );
}

// ─── Vendor dashboard ──────────────────────────────────────────────────────

function VendorDashboardBody() {
  const { navigate } = useRouter();

  const incomingRequests = [
    {
      id: 'v1',
      customer: 'Rena H.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
      title: 'Kitchen renovation — Port Baku',
      category: 'Renovation',
      budget: '18,000–24,000 ₼',
      posted: '14 min ago',
      match: 96,
    },
    {
      id: 'v2',
      customer: 'Elvin R.',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80',
      title: 'Full bathroom rebuild, 8 m²',
      category: 'Renovation',
      budget: '8,000–12,000 ₼',
      posted: '1 hour ago',
      match: 88,
    },
    {
      id: 'v3',
      customer: 'Leyla M.',
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80',
      title: 'Icherisheher duplex, turn-key',
      category: 'Renovation',
      budget: '40,000+ ₼',
      posted: '3 hours ago',
      match: 92,
    },
  ];

  const activeProjects = [
    {
      id: 'p1',
      title: 'Nizami boulevard flat — Signature',
      client: 'Aygun M.',
      progress: 72,
      stage: 'Installing finishes',
      due: 'Handover 2026-05-21',
    },
    {
      id: 'p2',
      title: 'Sabail townhouse — Considered',
      client: 'Rashad A.',
      progress: 34,
      stage: 'Trades',
      due: 'Handover 2026-06-18',
    },
  ];

  const recentReviews = [
    {
      id: 'rv1',
      author: 'Nigar S.',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=200&q=80',
      rating: 5,
      body: 'Weekly updates were calm and clear. The kitchen is the quietest room of the apartment now.',
    },
    {
      id: 'rv2',
      author: 'Tural H.',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80',
      rating: 5,
      body: 'They delivered the drawings exactly. No surprises on the final invoice.',
    },
  ];

  return (
    <PageSection paddingY="3rem">
      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1rem',
          marginBottom: '3rem',
        }}
        className="dash-stats"
      >
        <StatTile label="New requests" value="12" hint="3 high-fit" icon={<Inbox size={16} />} />
        <StatTile label="Active projects" value="4" hint="2 handovers this month" icon={<Briefcase size={16} />} />
        <StatTile label="This month" value="38,250 ₼" hint="+12% vs. last" icon={<Wallet size={16} />} />
        <StatTile label="Rating" value="4.9" hint="127 reviews" icon={<Star size={16} />} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr',
          gap: '2rem',
          alignItems: 'start',
        }}
        className="dash-body"
      >
        {/* Incoming requests + projects */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: '1.25rem',
              }}
            >
              <SectionTitle>Incoming requests</SectionTitle>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                Sorted by fit
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {incomingRequests.map((r) => (
                <div key={r.id} style={panelStyle}>
                  <div
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <Avatar src={r.avatar} size={48} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          marginBottom: '0.25rem',
                        }}
                      >
                        <div
                          style={{
                            fontFamily: 'var(--font-family-display)',
                            fontSize: '1.25rem',
                            fontWeight: 400,
                            lineHeight: 1.25,
                            letterSpacing: '-0.005em',
                          }}
                        >
                          {r.title}
                        </div>
                        <span
                          style={{
                            padding: '0.25rem 0.625rem',
                            background: 'var(--color-accent-rosegold)',
                            color: 'var(--color-text-inverse)',
                            borderRadius: '999px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.match}% fit
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                        {r.customer} · {r.category} · {r.posted}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--color-border-light)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      Budget <strong style={{ color: 'var(--color-text-primary)' }}>{r.budget}</strong>
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button variant="secondary" size="sm">
                        Decline
                      </Button>
                      <Button size="sm" onClick={() => navigate('offers')}>
                        Send a quote
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <SectionTitle>Active projects</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {activeProjects.map((p) => (
                <div key={p.id} style={panelStyle}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      marginBottom: '1rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontFamily: 'var(--font-family-display)',
                          fontSize: '1.25rem',
                          fontWeight: 400,
                          letterSpacing: '-0.005em',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {p.title}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                        {p.client} · {p.stage}
                      </div>
                    </div>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        fontSize: '0.8125rem',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      <Clock size={13} /> {p.due}
                    </span>
                  </div>
                  <div
                    style={{
                      height: '6px',
                      background: 'var(--color-background-surface)',
                      borderRadius: '999px',
                      overflow: 'hidden',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <div
                      style={{
                        width: `${p.progress}%`,
                        height: '100%',
                        background: 'var(--color-accent-rosegold)',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    <span>{p.progress}% complete</span>
                    <button
                      style={{
                        all: 'unset',
                        cursor: 'pointer',
                        color: 'var(--color-accent-rosegold)',
                        fontWeight: 500,
                      }}
                    >
                      Open project →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: earnings + reviews */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={panelStyle}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.25rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'var(--color-text-muted)',
                  fontWeight: 600,
                }}
              >
                Earnings this quarter
              </div>
              <TrendingUp size={14} style={{ color: 'var(--color-accent-rosegold)' }} />
            </div>
            <div
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: '2.25rem',
                fontWeight: 400,
                lineHeight: 1,
                letterSpacing: '-0.01em',
                marginBottom: '0.375rem',
              }}
            >
              112,480 ₼
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
              +18% vs. previous quarter
            </div>
            {/* tiny bar chart */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '0.375rem',
                height: '64px',
                marginTop: '1.25rem',
              }}
            >
              {[35, 52, 41, 68, 58, 74, 62, 80, 71, 88, 76, 92].map((h, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: `${h}%`,
                    background:
                      i === 11 ? 'var(--color-accent-rosegold)' : 'var(--color-background-surface)',
                    border: '1px solid var(--color-border-light)',
                    borderRadius: '2px',
                  }}
                />
              ))}
            </div>
          </div>

          <div style={panelStyle}>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                marginBottom: '1.25rem',
              }}
            >
              Recent reviews
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {recentReviews.map((r) => (
                <div key={r.id} style={{ display: 'flex', gap: '0.875rem' }}>
                  <Avatar src={r.avatar} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.25rem',
                      }}
                    >
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{r.author}</span>
                      <RatingLine rating={r.rating} size={12} />
                    </div>
                    <p
                      style={{
                        fontSize: '0.875rem',
                        lineHeight: 1.55,
                        color: 'var(--color-text-secondary)',
                        margin: 0,
                      }}
                    >
                      {r.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={panelStyle}>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                marginBottom: '1rem',
              }}
            >
              Categories you cover
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {categories.slice(0, 4).map((c) => (
                <span
                  key={c.id}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: 'var(--color-background-surface)',
                    border: '1px solid var(--color-border-light)',
                    borderRadius: '999px',
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {c.name}
                </span>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        @media (max-width: 1000px) {
          .dash-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .dash-body { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .dash-stats { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PageSection>
  );
}
